import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import chroma from "chroma-js"
import Send from "../../core/communication"
import TagsScreen from "../../screen/TagsScreen"
import { useForm } from "react-hook-form"
import { useScope } from "../SelectProjectCompanyContainer"
import { useSelector } from "react-redux"
import { useTags } from "../../core/utils/hooks/useTags"
import { useUsers } from "../../core/utils/hooks/useUsers"
import { customToast } from "../../components/Toast"
import { setData } from "../../core/redux/app/Actions"

const TagContainer = ({ users, isChatContainer,  tagIsOpen = false, setTagIsOpen = () => {}}) => {
  const [tags, setReloadTags] = useTags() || []
  const projects = useSelector(state => state.app.projects)
  const {reload, isRefetching } = useUsers()
  const [modalIsOpen, setModalIsOpen] = useState(tagIsOpen || false)
  const [selectedTag, setSelectedTag] = useState([])
  const [selectedTagAll, setSelectedTagAll] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [tagId, setTagId] = useState("")
  const [tagColor, setTagColor] = useState({
    tagBG: chroma("#2196f3").brighten(3).hex(),
    tagTextColor: "#2196f3",
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [isGeneral, setIsGeneral] = useState(false)
  const [newNotExactTags, setNewNotExactTags] = useState(false)
  const [allProjects, setAllProjects] = useState(false)
  const [loading, setLoading] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)
  const { scope } = useScope()
  const userAdmin = useSelector((state) => state.app?.user?.user?.roles?.role === "USER_ADMIN")

  const projectId = isChatContainer ? users[0]?.projectId : scope?.id
  const projectOptions = projects?.map(({id, description}) => ({label: description, value: id}))

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
  })

  const handleChangeColor = useCallback((color) => {
    setTagColor({
      tagBG: chroma(color.hex).brighten(2.5).hex(),
      tagTextColor: color.hex,
    });
  }, []);
  
  const handleSelectedTag = useCallback((e) => {
    const { checked, value } = e.target;
    setSelectedTag((prev) => (checked ? [...prev, value] : prev.filter((val) => val !== value)));
  }, []);
  
  const filteredTags = useMemo(() => {
    return tags
      ?.filter(tag => (
        (tag.projectId === projectId || tag.projectId === '')) && tag.name?.toLowerCase().includes(searchTerm?.toLowerCase()))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [tags, projectId, searchTerm]);

  const handleProjectTags = useCallback((data) => {
    setSelectedProjects(data);
    if (!data.length) {
      customToast("Selecione pelo menos um projeto para criar a tag", "error");
    }
  }, []);

  const onLinkTag = async () => {
    setLoading(true);
    
    try {
      const filteredTags = tags?.filter((item) => selectedTag.indexOf(item.id) !== -1);
      
      if (filteredTags.length > 0) {
        await Send.post("/tags-user", { users, tags: filteredTags });
        customToast("Tags vinculadas com sucesso!", "success");
      } else {
        customToast("Nenhuma tag selecionada para vincular.", "warning");
      }
      
      reload();
  
    } catch (error) {
      console.error("Erro ao vincular tags:", error);
      customToast("Erro ao vincular tags. Tente novamente.", "error");
      
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setModalIsOpen(true)
    setTagIsOpen(true)
  }

  const onEditTag = async (value) => {
    value.projectId === "" ? setIsGeneral(true) : setIsGeneral(false)
    setValue("name", value.name)
    setModalIsOpen(true)
    setTagId(value.id)
    setTagColor({
      tagBG: value.color,
      tagTextColor: value.text_color,
    })
  }

  const onRemoveTag = async (id) => {
    setRemoveLoading(true)
    let response
    
    if(id){
      response = await Send.delete(`/tags/${id}`)
    }
    else {
      response = await Send.post(`/tags-user/delete/all`, {tags: selectedTag, users: users.map(user => user.id)})
    }

    if(response.status === "success" || response.status === "OK"){
      reload()
      reloadTags()
      customToast("Vinculo excluido com sucesso", "success")
      
    }
    else {
      customToast("Não existe vinculo com a tag", "error")
      setRemoveLoading(false)
    }
  }

  const onCreateTag = async () => {
    setLoading(true);
    
    try {
      await Send.post("/tags", {
        name: searchTerm,
        color: chroma("#2196f3").brighten(2.5).hex(),
        text_color: "#2196f3",
        projects: allProjects ? projectOptions.map(({ value }) => value) : selectedProjects.map(({ value }) => value)
      });
  
      customToast("Tag criada com sucesso!", "success");
  
    } catch (error) {
      console.error("Erro ao criar tag:", error);
      customToast("Erro ao criar tag. Tente novamente.", "error");
    } finally {
      setLoading(false);
      setSearchTerm("");
      reloadTags();
      setSelectedTag([]);
      setAllProjects(false);
    }
  };

  const onUpdateTag = async (data) => {
    const { name } = data
    const project = isGeneral ? "" : projectId

    try {
      setLoading(true)
      await Send.put(`/tags/${tagId}`, {
        name,
        color: tagColor.tagBG,
        text_color: tagColor.tagTextColor,
        projectId: project,
      })
      setLoading(false)
      reload()
      reloadTags()
    } catch (error) {
      console.log(error)
    }

    setTagId("")
    setSelectedTag([])
  }

  const onSearchTags = (e) => {
    setSearchTerm(e.target.value)
    reset({ keepErrors: false })
  }

  const onChangeTagForAllProjects = (e) => {
    setIsGeneral(e.target.checked)
  }

  const dataTags = filteredTags || []

  const reloadTags = () => {
    setReloadTags((reloadTags) => !reloadTags)
  }

  const closeModal = () => {
    setModalIsOpen(false)
    setTagIsOpen(false)
    setTagId("")
    setValue("name", "")
    setSearchTerm("")
    setSelectedTag([])
    setNewNotExactTags(false)
    setSelectedProjects([])
    setAllProjects(false)
  }

  const handleSelectedTagAll = (e) => {
    setSelectedTag(
      e.target.checked ? dataTags?.map((tags) => tags.id) : []
    )
  };
  
/*   const handleProjectTags = (data) => {
    setSelectedProjects(data)

    if(!data.length){
      customToast("Selecione pelo menos um projeto para criar a tag", "error")
    }
  } */

  useEffect(() => {
    if(modalIsOpen){
      setReloadTags(true)
    }
  }, [modalIsOpen])

  useEffect(() => {
    setSelectedProjects(projectOptions?.filter((item) => item.value === projectId))
  }, [modalIsOpen])

  useEffect(() => {
    if(!isRefetching && !isChatContainer){
      setRemoveLoading(false)
      setLoading(false)
      closeModal()
    }
  }, [isRefetching, isChatContainer])

  const exactMatch = dataTags.some(tag => tag.name?.toLowerCase() === searchTerm?.toLowerCase());

  const handleNewTagNotExact = () => {
    setNewNotExactTags(true)
  }

  const handleCancelCreation = () => {
    setNewNotExactTags(false)
    setSearchTerm("")
    setValue("name", "")
    reset({ keepErrors: false })
  }

  return (
    <>
      <TagsScreen
        {...{
          tagId,
          openModal,
          exactMatch,
          handleNewTagNotExact,
          handleCancelCreation,
          newNotExactTags,
          modalIsOpen: modalIsOpen || tagIsOpen,
          isChatContainer,
          closeModal,
          onRemoveTag,
          onEditTag,
          onLinkTag,
          register,
          isValid,
          errors,
          onSearchTags,
          searchTerm,
          onCreateTag,
          onChangeTagForAllProjects,
          handleChangeColor,
          isGeneral,
          handleSelectedTag,
          selectedTag,
          handleSelectedTagAll,
          selectedTagAll,
          selectedUsers: users,
          selectedProjects,
          projectOptions,
          projectId,
          handleProjectTags,
          loading,
          removeLoading,
          setAllProjects,
          allProjects,
        }}
        isUserAdmin={userAdmin}
        data={dataTags}
        handleSubmit={handleSubmit(onUpdateTag)}
        selectedColor={tagColor}
      />
    </>
  )
}

export default memo(TagContainer)
